import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { User, UserRole, ApiResponse } from '@/types'

interface UserDependencies {
  goals: number
  action_plans: number
  bonuses: number
  has_dependencies: boolean
}

// Fetch all users (admin only)
export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: async (): Promise<User[]> => {
      const res = await fetch('/api/users')
      const data: ApiResponse<User[]> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to fetch users')
      return data.data!
    },
  })
}

// Update user role (admin only)
export function useUpdateUserRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: UserRole }) => {
      const res = await fetch(`/api/users/${userId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      })
      const data: ApiResponse<User> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to update user role')
      return data.data!
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

// Fetch user dependencies before deletion (admin only)
export function useUserDependencies(userId: string | null) {
  return useQuery({
    queryKey: ['user-dependencies', userId],
    queryFn: async (): Promise<UserDependencies> => {
      const res = await fetch(`/api/users/${userId}/dependencies`)
      const data: ApiResponse<UserDependencies> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to fetch dependencies')
      return data.data!
    },
    enabled: !!userId,
  })
}

// Delete user (admin only)
export function useDeleteUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await fetch(`/api/users/${userId}`, { method: 'DELETE' })
      const data: ApiResponse<void> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to delete user')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}
